import React, { useContext, useEffect, useState } from "react";
import { PDFDownloadLink, PDFViewer } from "@react-pdf/renderer";
import Button from "@mui/material/Button";
import { Grid, useMediaQuery } from "@mui/material";
import TicketPdfSignature from "../../../Components/ticketPdfSignature";
import { TicketErrorHelper } from "../../../Utils/TicketErrorHelper";
import TicketValidator, { FAILED_STALE, PARTIAL_SYNC } from "../../../Utils/TicketValidator";
import { TripTicketJsonPdf } from "../../../sharedCode/TripTicketJsonPdf";

import { JobExtended } from "../../../Utils/TypeExtensions";
import { findById } from "../../../Utils/ContextLookups";
import dayjs from "dayjs";
import { getTicketNumber, IsDemoMode } from "../../../sharedCode/HelperMethods";
import { IDENTITY_BREADCRUMB } from "../../../App";
import { TicketProps } from "../FlightLegs/ticketFlightLegs";
import { useLiveQuery } from "dexie-react-hooks";
import { saveTicketChanges, TicketDatabase } from "../../../Utils/TicketDatabase";
import { isSuperUser } from "../../../Components/validation/newHelpers";
import { GlobalContext } from "../../../Utils/Contexts";

interface Props extends TicketProps {
    closeDialog(): void;
}

export function TicketPdf(props: Props): JSX.Element {
    const context = useContext(GlobalContext);
    const [showAcknowledgement, setShowAcknowledgement] = useState(false);
    const [viewportSize, setViewportSize] = useState({ width: 0, height: 0 });

    const isOnTablet = !useMediaQuery("@media (pointer: fine)");
    // need to use less of the screen on a shorter device
    // TODO - there has to be a better way, oof
    const verticalPercent = Math.min(75, 45 + (viewportSize.height - 400) / 20);
    const pdfHeight = verticalPercent.toFixed(0) + "vh";

    useEffect(() => {
        const handleResize = () => {
            setViewportSize({ width: window.innerWidth, height: window.innerHeight });
        };
        window.addEventListener("resize", handleResize);
        handleResize();

        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, []);

    const aircraftList = props.aircraftList.slice();
    const ameList = props.ameList.slice();
    const companyList = props.companyList.slice();
    const lineItemTypeList = props.lineItemTypeList.slice();
    const minimumsRequiredList = props.minimumsRequiredList.slice();
    const pilotList = props.pilotList.slice();
    const ticketList = useLiveQuery(async () => TicketDatabase.ticket.toArray());

    if (!aircraftList || !ameList || !companyList || !lineItemTypeList || !minimumsRequiredList || !pilotList || !ticketList) return <></>;

    const isReadyForSigning = TicketErrorHelper.isApprovable(props.errors);
    const canSend = TicketValidator.IsReadOnly(props.ticket) || props.ticket.pushAttempts === FAILED_STALE || props.ticket.pushAttempts === PARTIAL_SYNC;

    const personList = [...ameList, ...pilotList];
    const job = props.jobList.find((j) => {
        return j.id === props.ticket.job_id;
    }) as JobExtended;

    const breadcrumb = localStorage.getItem(IDENTITY_BREADCRUMB) ?? "NO BREADCRUMB";
    const shouldShowDataDisclaimer = TicketValidator.ShouldShowDataDisclaimer(props.ticket, props.errors);

    const ticketPdf = TripTicketJsonPdf({
        ticket: props.ticket,
        job,
        aircraftList,
        companyList,
        lineItemTypeList,
        minimumsRequiredList,
        personList,
        // allow the PDF to be sent when warning level messages exist
        // and once a ticket is read-only show the PDF even if errors have shown up post-syncing
        // for stalled sync (FAILED_STALE/PARTIAL_SYNC) still pass validation results so errors/warnings section shows
        validationResults: props.errors.length > 0 && (!TicketValidator.IsReadOnly(props.ticket) || TicketValidator.IsStalledSyncStatus(props.ticket)) ? props.errors : [],
        breadcrumb,
        showDataDisclaimer: shouldShowDataDisclaimer,
        isSuperUser: isSuperUser(context.loggedInUser),
    });

    let signButtonText = "FIX ERRORS BEFORE SIGNING";

    if (isReadyForSigning) {
        signButtonText = "SIGN";
    }

    function handleSignClicked() {
        if (isReadyForSigning) {
            setShowAcknowledgement(true);
        } else {
            props.closeDialog();
        }
    }

    function handleUnsignClicked() {
        if (IsDemoMode()) {
            const toMerge = {
                Signatures: [],
            };

            saveTicketChanges(props.ticket.id, toMerge).catch(() => props.showForceReloadDialogue());
        }
    }

    const pilot = findById(props.pilotList, props.ticket.pilot_person_id);
    const pilotIdentifier = (pilot?.first_name ?? " ").substring(0, 1) + (pilot?.last_name ?? " ").substring(0, 1);
    const aircraftIdentifier = findById(props.aircraftList, props.ticket.aircraft_id)?.registration;
    const dateIdentifier = dayjs(props.ticket.date).format("YYYYMMDD");
    const ticketNumber = getTicketNumber(props.ticket);

    const descriptiveName = `${dateIdentifier}-${ticketNumber}-${aircraftIdentifier}-${pilotIdentifier}.pdf`;

    // NB Firefox is ignoring the setting of showToolbar={false}
    return (
        <>
            {showAcknowledgement && <TicketPdfSignature {...props} setShowApprove={setShowAcknowledgement} />}
            <div style={{ height: pdfHeight }}>
                <PDFViewer showToolbar={false} width={"100%"} height={"100%"}>
                    {ticketPdf}
                </PDFViewer>
            </div>
            {!canSend && (
                <Grid container justifyContent={"center"}>
                    <Button sx={{ mt: 1.5 }} color={isReadyForSigning ? "primary" : "error"} variant={"contained"} onClick={handleSignClicked}>
                        {signButtonText}
                    </Button>
                </Grid>
            )}
            {(canSend && TicketValidator.IsComplete(props.ticket)) || props.ticket.pushAttempts === FAILED_STALE || props.ticket.pushAttempts === PARTIAL_SYNC ? (
                <Grid container justifyContent={"center"}>
                    <PDFDownloadLink style={{ textDecoration: "none" }} document={ticketPdf} fileName={descriptiveName}>
                        {({ blob, url, loading, error }) =>
                            loading ? (
                                <Button sx={{ mt: 1.5 }} variant={"contained"}>
                                    GENERATING PDF...
                                </Button>
                            ) : isOnTablet ? (
                                <Button sx={{ mt: 1.5 }} variant={"contained"}>
                                    EMAIL PDF
                                </Button>
                            ) : (
                                <Button sx={{ mt: 1.5 }} variant={"contained"}>
                                    DOWNLOAD PDF
                                </Button>
                            )
                        }
                    </PDFDownloadLink>
                </Grid>
            ) : null}
            {canSend && IsDemoMode() && (
                <Grid container justifyContent={"center"}>
                    <Button sx={{ mt: 1.5 }} color={isReadyForSigning ? "primary" : "error"} variant={"contained"} onClick={handleUnsignClicked}>
                        UN-SIGN (DEMO MODE ONLY)
                    </Button>
                </Grid>
            )}
        </>
    );
}
